import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import type { ProjectFolder } from "../../api/projectFolders";
import type { ApiError } from "../../types/api";
import type { Project } from "../../types/project";
import FolderBreadcrumbs, { type FolderActions } from "./FolderBreadcrumbs";
import type { InlineNameEdit } from "./InlineNameField";
import FolderRow from "./FolderRow";
import NewFolderRow from "./NewFolderRow";
import ArchiveRow from "./ArchiveRow";
import NewProjectRow from "./NewProjectRow";
import ProjectRow from "./ProjectRow";
import UpRow from "./UpRow";
import {
  type DragItem,
  type FolderIndex,
  type FolderKey,
  canDrop,
  hiddenByFolder,
  normalizeFolderId,
  pathLabel,
  subtreeCounts,
} from "./folderTree";
import { FROZEN_RIGHT, frozenHeadSx } from "./frozenColumns";
import { type SortKey, type SortSpec, nextSort } from "./sortRows";
import { screenToLayout } from "../../utils/uiZoom";

/** dataTransfer type for rows dragged within the listing. */
export const DND_MIME = "application/x-mitigate-listing";

interface Column {
  key: SortKey | null;
  label: string;
  align?: "right";
  searchOnly?: boolean;
  /** Frozen to the right edge at this offset (see frozenColumns.ts). */
  frozenRight?: number;
  edgePad?: boolean;
}

// Data columns scroll; the last four (pin, eye, show on map, menu) stay put.
// The Folder column shows whenever rows come from several folders: a search,
// or the Archive.
const COLUMNS: Column[] = [
  { key: "name", label: "Project" },
  { key: "folder", label: "Folder", searchOnly: true },
  { key: "updated", label: "Updated" },
  { key: "owner", label: "Owner" },
  { key: "strategies", label: "Strategies", align: "right" },
  { key: "simulations", label: "Fire sims", align: "right" },
  { key: "curing", label: "Curing", align: "right" },
  { key: null, label: "Tags" },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.pin },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.eye },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.locate },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.menu, edgePad: true },
];

/** The Archive, a folder-like place at the top level for archived projects. */
export interface ArchivePlace {
  /** The Archive is what the pane is showing. */
  open: boolean;
  /** Archived projects (every folder's). */
  count: number;
  /** Stories of archived projects (story-map browse mode). */
  storyCount?: number;
  canLocate: boolean;
  onOpen: () => void;
  onLocate: () => void;
}

/** A folder or project being named in its own row before it exists. */
export interface NewRowDraft extends InlineNameEdit {
  /** Changes for each new draft, so the row starts fresh. */
  key: number;
  initialName: string;
}

/** A folder whose name is being edited in place: a row, or the open folder's crumb. */
export interface FolderRename {
  folderId: number;
  edit: InlineNameEdit;
}

/** Rows to keep between a map-hovered row and the pane's top or bottom edge. */
const SCROLL_CONTEXT_ROWS = 2;

export interface ProjectTablePaneProps {
  mode: "explorer" | "search";
  index: FolderIndex;
  openFolderId: FolderKey;
  /** Sub-folders of the open folder (explorer mode). */
  folders: ProjectFolder[];
  /** The project rows, already filtered and sorted. */
  projects: Project[];
  /** Every active project, for folder counts. */
  allProjects: Project[];
  isLoading: boolean;
  error: ApiError | null;
  highlightedProjectId: string | null;
  highlightedFolderId: number | null;
  /** Set when the map's hover settled: scroll this row into view. */
  scrollToProjectId: string | null;
  sort: SortSpec | null;
  onSortChange: (sort: SortSpec | null) => void;
  viewOnly: boolean;
  searchSummary?: { count: number; onClear: () => void };
  folderActions: FolderActions | null;
  onOpenFolder: (id: FolderKey) => void;
  onHoverProject: (id: string | null) => void;
  onHoverFolder: (id: number | null) => void;
  onDraggingChange: (dragging: boolean) => void;
  onMoveProject: (projectId: string, target: FolderKey) => void;
  onMoveFolder: (folderId: number, target: FolderKey) => void;
  onRequestRenameFolder: (folder: ProjectFolder) => void;
  onRequestMoveFolder: (folder: ProjectFolder) => void;
  onRequestDeleteFolder: (folder: ProjectFolder) => void;
  /** Open the boundary-layer dialog for a folder. */
  onRequestFolderBoundary: (folder: ProjectFolder) => void;
  onRequestMoveProject: (project: Project) => void;
  /** Fly the map to this project. */
  onLocateProject: (project: Project) => void;
  onToggleProjectMapVisible: (project: Project) => void;
  /** Open the dialog that styles a project's property boundaries. */
  onRequestPropertyStyle: (project: Project) => void;
  onToggleFolderMapVisible: (folder: ProjectFolder) => void;
  onToggleFolderPin: (folder: ProjectFolder) => void;
  onLocateFolder: (folder: ProjectFolder) => void;
  folderHasLocation: (folder: ProjectFolder) => boolean;
  /** A folder being named before it is created, shown first in the folder list. */
  newFolder?: NewRowDraft | null;
  /** A project being named before it is set up, shown first in the project list. */
  newProject?: NewRowDraft | null;
  renamingFolder?: FolderRename | null;
  /** The "only what's on the map" filter (switched on from the map): how many rows it hides. */
  viewFilter?: {
    active: boolean;
    hiddenProjects: number;
    hiddenFolders: number;
    onShowAll: () => void;
  };
  /** Rendered at the right-hand end of the breadcrumbs strip (the Projects | Story maps switch). */
  headerEnd?: React.ReactNode;
  /** The Archive: its row at the top level, or the pane's contents when open. */
  archive?: ArchivePlace & {
    /** A project row dropped on the Archive row. */
    onArchiveProject: (projectId: string) => void;
    /** An archived row dropped on "All projects" (or restored from its menu). */
    onRestoreProject: (project: Project) => void;
    onDeleteProjectPermanently: (project: Project) => void;
    canDeletePermanently: boolean;
  };
}

/**
 * The right-hand pane: breadcrumb trail, then sub-folders, then project rows.
 * Owns the drag-and-drop plumbing and the "scroll this row into view" effect;
 * everything that changes data is a callback to the split view.
 */
export default function ProjectTablePane({
  mode,
  index,
  openFolderId,
  folders,
  projects,
  allProjects,
  isLoading,
  error,
  highlightedProjectId,
  highlightedFolderId,
  scrollToProjectId,
  sort,
  onSortChange,
  viewOnly,
  searchSummary,
  folderActions,
  onOpenFolder,
  onHoverProject,
  onHoverFolder,
  onDraggingChange,
  onMoveProject,
  onMoveFolder,
  onRequestRenameFolder,
  onRequestMoveFolder,
  onRequestDeleteFolder,
  onRequestFolderBoundary,
  onRequestMoveProject,
  onLocateProject,
  onToggleProjectMapVisible,
  onRequestPropertyStyle,
  onToggleFolderMapVisible,
  onToggleFolderPin,
  onLocateFolder,
  folderHasLocation,
  newFolder,
  newProject,
  renamingFolder,
  viewFilter,
  headerEnd,
  archive,
}: ProjectTablePaneProps) {
  const archiveOpen = !!archive?.open;
  const columns = COLUMNS.filter((c) => !c.searchOnly || mode === "search" || archiveOpen);
  const colCount = columns.length;

  // -- scroll-into-view from a map hover ------------------------------------
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  // Browsers fire mouseenter when content moves under a stationary pointer,
  // so hovers are ignored briefly around a programmatic scroll.
  const suppressUntilRef = useRef(0);
  useEffect(() => {
    if (!scrollToProjectId) return;
    const el = rowRefs.current[scrollToProjectId];
    const container = containerRef.current;
    if (!el || !container) return;
    // Bring the row a couple of rows in from whichever edge it is beyond, so
    // it lands with its neighbours visible rather than clipped at the edge.
    const header = container.querySelector("thead")?.getBoundingClientRect().height ?? 0;
    const margin = el.offsetHeight * SCROLL_CONTEXT_ROWS;
    const box = container.getBoundingClientRect();
    const row = el.getBoundingClientRect();
    const topLimit = box.top + header + margin;
    const bottomLimit = box.bottom - margin;
    let delta = 0;
    if (row.top < topLimit) delta = row.top - topLimit;
    else if (row.bottom > bottomLimit) delta = row.bottom - bottomLimit;
    if (delta === 0) return;
    suppressUntilRef.current = performance.now() + 400;
    // delta is in screen px (client rects); scrollBy takes layout px.
    container.scrollBy({ top: screenToLayout(delta), behavior: "smooth" });
  }, [scrollToProjectId]);

  // A new folder's row is the first folder row: bring it into view.
  const showNewFolder = !!newFolder && mode === "explorer" && !isLoading;
  const newFolderKey = showNewFolder && newFolder ? newFolder.key : null;
  useEffect(() => {
    if (newFolderKey !== null) containerRef.current?.scrollTo({ top: 0 });
  }, [newFolderKey]);

  // -- drag and drop (native HTML5, no library) -----------------------------
  const dragItemRef = useRef<DragItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);

  const hoverProject = useCallback(
    (id: string) => (hovering: boolean) => {
      if (!hovering) {
        onHoverProject(null);
        return;
      }
      if (dragItemRef.current || performance.now() < suppressUntilRef.current) return;
      onHoverProject(id);
    },
    [onHoverProject]
  );
  const hoverFolder = useCallback(
    (id: number) => (hovering: boolean) => {
      if (!hovering) {
        onHoverFolder(null);
        return;
      }
      if (dragItemRef.current) return;
      onHoverFolder(id);
    },
    [onHoverFolder]
  );

  // Called from dragend AND from a successful drop. A dropped row leaves the
  // list at once (the move is optimistic), and a browser never delivers
  // dragend to an element that is no longer in the document, so relying on
  // dragend alone left the grabbing cursor stuck and hovers switched off.
  const finishDrag = useCallback(() => {
    dragItemRef.current = null;
    setDropTargetKey(null);
    setIsDragging(false);
    onDraggingChange(false);
  }, [onDraggingChange]);

  const dragPropsFor = (item: DragItem): React.HTMLAttributes<HTMLElement> & { draggable?: boolean } =>
    viewOnly
      ? {}
      : {
          draggable: true,
          onDragStart: (e) => {
            // Safari needs a non-empty payload for the drag to start at all.
            e.dataTransfer.setData(DND_MIME, JSON.stringify(item));
            e.dataTransfer.effectAllowed = "move";
            dragItemRef.current = item;
            setIsDragging(true);
            onDraggingChange(true);
          },
          onDragEnd: finishDrag,
        };

  // Dropping a project on the Archive row archives it; dropping an archived
  // row on "All projects" (the up row or the root crumb) restores it. Both
  // sidestep the folder rules: an archived project keeps its folder.
  const archiveDropProps = useCallback(
    (onDrop: (item: DragItem) => void, key: string): React.HTMLAttributes<HTMLElement> => ({
      onDragOver: (e) => {
        const item = dragItemRef.current;
        if (!item || item.kind !== "project") return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDropTargetKey((prev) => (prev === key ? prev : key));
      },
      onDragLeave: () => setDropTargetKey((prev) => (prev === key ? null : prev)),
      onDrop: (e) => {
        e.preventDefault();
        const item = dragItemRef.current;
        finishDrag();
        if (item?.kind === "project") onDrop(item);
      },
    }),
    [finishDrag]
  );
  const restoreDropPropsFor = useCallback(
    (target: FolderKey, key: string): React.HTMLAttributes<HTMLElement> =>
      target === null && archive
        ? archiveDropProps((item) => {
            const row = projects.find((p) => String(p.id) === item.id);
            if (row) archive.onRestoreProject(row);
          }, key)
        : {},
    [archive, archiveDropProps, projects]
  );

  const dropPropsFor = useCallback(
    (target: FolderKey, key: string): React.HTMLAttributes<HTMLElement> => ({
      onDragOver: (e) => {
        const item = dragItemRef.current;
        // Without preventDefault the browser never fires drop; leaving it out
        // for an invalid target is what shows the not-allowed cursor.
        if (!item || !canDrop(index, item, target)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDropTargetKey((prev) => (prev === key ? prev : key));
      },
      onDragLeave: () => setDropTargetKey((prev) => (prev === key ? null : prev)),
      onDrop: (e) => {
        e.preventDefault();
        const item = dragItemRef.current;
        finishDrag();
        if (!item || !canDrop(index, item, target)) return;
        if (item.kind === "project") onMoveProject(item.id, target);
        else onMoveFolder(item.id, target);
      },
    }),
    [index, onMoveProject, onMoveFolder, finishDrag]
  );

  const parentOfOpen: FolderKey = openFolderId === null ? null : (index.byId.get(openFolderId)?.parent ?? null);
  const parentName = parentOfOpen === null ? "All projects" : index.byId.get(parentOfOpen)?.name ?? "the folder above";
  // Always present inside a folder (like a file manager's ".."): a way up, and
  // the drop target for moving things out. It must NOT appear only while
  // dragging: inserting a row into the table during dragstart shifts the
  // dragged row and makes the browser abandon the drag.
  const showUpRow = mode === "explorer" && (openFolderId !== null || archiveOpen);
  // The Archive sits among the top-level folders.
  const showArchiveRow = !!archive && mode === "explorer" && openFolderId === null && !archiveOpen;

  const showNewProject = !!newProject && !isLoading && !archiveOpen;
  const explorerEmpty =
    mode === "explorer" && folders.length === 0 && projects.length === 0 && !showNewFolder && !showNewProject && !showArchiveRow;
  const searchEmpty = mode === "search" && projects.length === 0 && !showNewProject;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 48,
        }}
      >
        {mode === "search" && searchSummary ? (
          <>
            <Typography sx={{ fontWeight: 700 }}>
              Search results · {searchSummary.count} project{searchSummary.count === 1 ? "" : "s"}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={searchSummary.onClear} sx={{ textTransform: "none" }}>
              Clear search
            </Button>
          </>
        ) : (
          <FolderBreadcrumbs
            index={index}
            openFolderId={openFolderId}
            onOpen={onOpenFolder}
            dropPropsFor={isDragging ? (archiveOpen ? restoreDropPropsFor : dropPropsFor) : undefined}
            dropTargetKey={dropTargetKey}
            actions={archiveOpen ? null : folderActions}
            rename={renamingFolder && renamingFolder.folderId === openFolderId ? renamingFolder.edit : null}
            archive={archiveOpen}
          />
        )}
        {headerEnd && <Box sx={{ flexShrink: 0, ml: 1 }}>{headerEnd}</Box>}
      </Box>

      {viewFilter?.active && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.5,
            backgroundColor: "rgba(0,212,173,0.08)",
            borderBottom: 1,
            borderColor: "divider",
            fontSize: "0.8rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {viewFilter.hiddenProjects + viewFilter.hiddenFolders > 0
              ? `${[
                  viewFilter.hiddenProjects
                    ? `${viewFilter.hiddenProjects} project${viewFilter.hiddenProjects === 1 ? "" : "s"}`
                    : null,
                  viewFilter.hiddenFolders
                    ? `${viewFilter.hiddenFolders} folder${viewFilter.hiddenFolders === 1 ? "" : "s"}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" and ")} out of view on the map`
              : "Listing only what's on the map"}
          </Typography>
          <Button size="small" onClick={viewFilter.onShowAll} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
            Show all
          </Button>
        </Box>
      )}

      <TableContainer ref={containerRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {/* Wider than a half pane on purpose: the data columns scroll sideways
            under the frozen controls instead of squeezing. */}
        <Table stickyHeader size="small" sx={{ tableLayout: "auto", minWidth: 820 }}>
          <TableHead>
            <TableRow>
              {columns.map((c, i) => {
                const key = c.key;
                return (
                  <TableCell
                    key={`${c.label}-${i}`}
                    align={c.align}
                    sx={{
                      whiteSpace: "nowrap",
                      fontWeight: 700,
                      ...(c.frozenRight !== undefined ? frozenHeadSx(c.frozenRight, c.edgePad) : {}),
                    }}
                  >
                    {key ? (
                      <Tooltip
                        title={key === "name" ? "Pinned projects stay at the top whichever way you sort" : ""}
                        disableHoverListener={key !== "name"}
                      >
                        <TableSortLabel
                          active={sort?.key === key}
                          direction={sort?.key === key ? sort.dir : "asc"}
                          onClick={() => onSortChange(nextSort(sort, key))}
                        >
                          {c.label}
                        </TableSortLabel>
                      </Tooltip>
                    ) : (
                      c.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ color: "text.secondary" }}>
                  Loading projects…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && error && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ color: "error.main" }}>
                  Couldn't load projects: {error.message}
                </TableCell>
              </TableRow>
            )}
            {showUpRow && (
              <UpRow
                parentName={archiveOpen ? "All projects" : parentName}
                colSpan={colCount}
                onClick={() => onOpenFolder(archiveOpen ? null : parentOfOpen)}
                isDragging={isDragging}
                isDropTarget={dropTargetKey === "up"}
                dropProps={archiveOpen ? restoreDropPropsFor(null, "up") : dropPropsFor(parentOfOpen, "up")}
              />
            )}
            {showArchiveRow && archive && (
              <ArchiveRow
                count={archive.count}
                storyCount={archive.storyCount}
                colSpan={colCount}
                highlighted={false}
                isDropTarget={dropTargetKey === "archive"}
                onOpen={archive.onOpen}
                onHoverChange={() => undefined}
                canLocate={archive.canLocate}
                onLocate={archive.onLocate}
                dropProps={archiveDropProps((item) => archive.onArchiveProject(String(item.id)), "archive")}
              />
            )}
            {showNewFolder && newFolder && (
              <NewFolderRow
                key={`new-folder-${newFolder.key}`}
                initialName={newFolder.initialName}
                colSpan={colCount}
                busy={newFolder.busy}
                error={newFolder.error}
                onCommit={newFolder.onCommit}
                onCancel={newFolder.onCancel}
              />
            )}
            {!isLoading &&
              mode === "explorer" &&
              folders.map((folder) => (
                <FolderRow
                  key={`folder-${folder.id}`}
                  folder={folder}
                  counts={subtreeCounts(index, allProjects, folder.id)}
                  colSpan={colCount}
                  viewOnly={viewOnly}
                  isDropTarget={dropTargetKey === `folder:${folder.id}`}
                  highlighted={highlightedFolderId === folder.id}
                  isPinned={!!folder.is_pinned}
                  onTogglePin={() => onToggleFolderPin(folder)}
                  canLocate={folderHasLocation(folder)}
                  onLocate={() => onLocateFolder(folder)}
                  mapVisible={folder.map_visible !== false}
                  hiddenBy={hiddenByFolder(index, folder.parent)?.name ?? null}
                  onToggleMapVisible={() => onToggleFolderMapVisible(folder)}
                  onOpen={() => onOpenFolder(folder.id)}
                  onHoverChange={hoverFolder(folder.id)}
                  onRename={() => onRequestRenameFolder(folder)}
                  rename={renamingFolder?.folderId === folder.id ? renamingFolder.edit : null}
                  onMove={() => onRequestMoveFolder(folder)}
                  onDelete={() => onRequestDeleteFolder(folder)}
                  onBoundary={() => onRequestFolderBoundary(folder)}
                  hasBoundary={!!folder.boundary_bbox}
                  dragProps={dragPropsFor({ kind: "folder", id: folder.id, parentId: openFolderId })}
                  dropProps={dropPropsFor(folder.id, `folder:${folder.id}`)}
                />
              ))}
            {showNewProject && newProject && (
              <NewProjectRow
                key={`new-project-${newProject.key}`}
                initialName={newProject.initialName}
                colSpan={colCount}
                busy={newProject.busy}
                error={newProject.error}
                onCommit={newProject.onCommit}
                onCancel={newProject.onCancel}
              />
            )}
            {!isLoading &&
              projects.map((project) => {
                const id = String(project.id);
                const folderId = normalizeFolderId(index, project.folder);
                return (
                  <ProjectRow
                    key={id}
                    project={project}
                    showFolder={mode === "search" || archiveOpen}
                    folderLabel={pathLabel(index, folderId)}
                    highlighted={highlightedProjectId === id}
                    viewOnly={viewOnly}
                    rowRef={(el) => {
                      rowRefs.current[id] = el;
                    }}
                    onHoverChange={hoverProject(id)}
                    onMoveToFolder={() => onRequestMoveProject(project)}
                    onLocate={() => onLocateProject(project)}
                    mapVisible={project.map_visible !== false}
                    hiddenBy={hiddenByFolder(index, folderId)?.name ?? null}
                    onToggleMapVisible={() => onToggleProjectMapVisible(project)}
                    onStyleProperties={() => onRequestPropertyStyle(project)}
                    dragProps={dragPropsFor({ kind: "project", id, folderId })}
                    archived={archiveOpen}
                    onRestore={archive ? () => archive.onRestoreProject(project) : undefined}
                    onDeletePermanently={archive ? () => archive.onDeleteProjectPermanently(project) : undefined}
                    canDeletePermanently={archive?.canDeletePermanently ?? false}
                  />
                );
              })}
            {!isLoading && !error && (explorerEmpty || searchEmpty) && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
                  {searchEmpty
                    ? "No projects match your search."
                    : archiveOpen
                      ? "Nothing archived. Drag a project onto the Archive, or archive it from its row menu."
                      : openFolderId === null
                        ? "No projects yet."
                        : "This folder is empty."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
